import { useMemo, useState } from 'react'
import { ActionIcon, Alert, Badge, Button, Loader } from '@mantine/core'
import dayjs from 'dayjs'
import { IconAlertTriangle, IconFilter, IconRefresh } from '@tabler/icons-react'
import {
  activeJourneyFilterCount,
  DEFAULT_JOURNEY_FILTERS,
  useCareJourneys,
  type JourneyFilters,
  type JourneyKpi,
} from './journeys'
import { JOURNEY_COLOR, JOURNEY_LEGEND, MONO, SANS } from './journeyTheme'
import { JourneyCard } from './JourneyCard'
import { JourneyKpiCards } from './JourneyKpiCards'
import { JourneyFilterDrawer } from './JourneyFilterDrawer'
import { JourneyPatientSelect } from './JourneyPatientSelect'
import { KpiDetailDrawer } from './KpiDetailDrawer'

export const CareJourneysTab = () => {
  const [drawerFilters, setDrawerFilters] = useState<JourneyFilters>(DEFAULT_JOURNEY_FILTERS)
  // Patient lives outside the drawer's draft so it can apply on selection —
  // that is the whole point of moving it to the toolbar. Kept separate rather
  // than folded into `drawerFilters` so a drawer Apply, which replaces the
  // whole filter object, cannot silently drop the patient you picked.
  const [patientId, setPatientId] = useState('')
  const [filtersOpen, setFiltersOpen] = useState(false)
  // The KPI whose drawer is open.
  const [openKpi, setOpenKpi] = useState<JourneyKpi | null>(null)
  // The KPI the board is narrowed to, set by the drawer's "View all" action.
  const [focusedKpi, setFocusedKpi] = useState<JourneyKpi | null>(null)
  const filters = useMemo(
    () => ({ ...drawerFilters, ...(patientId ? { patientIds: [patientId] } : {}) }),
    [drawerFilters, patientId],
  )
  const { data, isLoading, isFetching, refetch, error } = useCareJourneys(filters)

  const journeys = useMemo(() => data?.journeys ?? [], [data])
  const drawerJourneys = useMemo(
    () => (openKpi ? journeys.filter((journey) => journey.kpis.includes(openKpi)) : []),
    [journeys, openKpi],
  )
  const visibleJourneys = useMemo(
    () => (focusedKpi ? journeys.filter((journey) => journey.kpis.includes(focusedKpi)) : journeys),
    [journeys, focusedKpi],
  )

  // Counts only what the DRAWER owns. The patient picker sits in plain sight on
  // the toolbar with its own clear button, so counting it here would inflate a
  // badge whose only affordance cannot clear it.
  const filterCount = activeJourneyFilterCount(drawerFilters)
  const scopeLine = `${visibleJourneys.length} ${
    visibleJourneys.length === 1 ? 'journey' : 'journeys'
  } · ${filterCount > 0 ? `${filterCount} filter${filterCount === 1 ? '' : 's'} active` : 'all owners'}`

  return (
    <div className="flex flex-col gap-4" style={{ fontFamily: SANS }}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <div
            className="text-[21px] font-semibold tracking-[-0.01em]"
            style={{ color: JOURNEY_COLOR.ink }}
          >
            Care Journeys
          </div>
          <div
            className="text-[11.5px] tracking-[0.03em]"
            style={{ fontFamily: MONO, color: JOURNEY_COLOR.muted }}
          >
            {scopeLine}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <JourneyPatientSelect
            className="w-[240px]"
            patients={data?.filterOptions.patients}
            value={patientId}
            onChange={(next) => {
              setPatientId(next)
              // A new scope invalidates a KPI focus taken under the old one.
              setFocusedKpi(null)
            }}
          />
          <Button
            variant="default"
            size="compact-md"
            leftSection={<IconFilter size={15} />}
            rightSection={
              filterCount > 0 ? (
                <Badge size="xs" variant="filled">
                  {filterCount}
                </Badge>
              ) : undefined
            }
            onClick={() => setFiltersOpen(true)}
          >
            Filters
          </Button>
          {data ? (
            <span
              className="text-[11.5px]"
              style={{ fontFamily: MONO, color: JOURNEY_COLOR.muted }}
            >
              Updated {dayjs().format('MMM D · h:mm A')}
            </span>
          ) : null}
          <ActionIcon
            variant="subtle"
            onClick={() => refetch()}
            loading={isFetching && !isLoading}
            aria-label="Refresh care journeys"
          >
            <IconRefresh size={17} />
          </ActionIcon>
        </div>
      </div>

      {error ? (
        <Alert
          color="red"
          icon={<IconAlertTriangle size={16} />}
          title="Could not load care journeys"
        >
          {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      ) : null}

      {data?.truncated ? (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
          Showing the most recent visits only — narrow the date range for a complete picture.
        </Alert>
      ) : null}

      <JourneyKpiCards
        kpis={
          data?.kpis ?? {
            active: 0,
            awaitingResults: 0,
            awaitingResultsOverdue: 0,
            resultsNeedFollowUp: 0,
            completed: 0,
          }
        }
        selected={openKpi}
        onSelect={setOpenKpi}
      />

      {focusedKpi ? (
        <div className="flex items-center gap-2">
          <span className="text-sm" style={{ color: JOURNEY_COLOR.muted }}>
            Showing only patients counted under this KPI.
          </span>
          <Button size="compact-sm" variant="subtle" onClick={() => setFocusedKpi(null)}>
            Show all patients
          </Button>
        </div>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : visibleJourneys.length ? (
        <div className="flex flex-col gap-4">
          {visibleJourneys.map((journey) => (
            <JourneyCard key={journey.patientId} journey={journey} />
          ))}
        </div>
      ) : (
        <div
          className="rounded-[14px] bg-white p-10 text-center"
          style={{ border: `1px solid ${JOURNEY_COLOR.border}` }}
        >
          <p className="text-sm" style={{ color: JOURNEY_COLOR.muted }}>
            No care journeys match the current filters.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-6 px-1 py-1.5">
        {JOURNEY_LEGEND.map((entry) => (
          <div key={entry.label} className="flex items-center gap-2">
            <div className="h-[7px] w-[7px] rounded-full" style={{ background: entry.color }} />
            <div className="text-[12px]" style={{ color: JOURNEY_COLOR.subtle }}>
              {entry.label}
            </div>
          </div>
        ))}
      </div>

      <JourneyFilterDrawer
        opened={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filterOptions={data?.filterOptions}
        onApply={(next) => {
          setDrawerFilters(next)
          // A new filter scope invalidates a KPI focus taken under the old one.
          setFocusedKpi(null)
        }}
      />

      <KpiDetailDrawer
        kpi={openKpi}
        journeys={drawerJourneys}
        onClose={() => setOpenKpi(null)}
        onViewAll={() => {
          setFocusedKpi(openKpi)
          setOpenKpi(null)
        }}
      />
    </div>
  )
}
